import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Category, Product, StoreSettings } from '@/types'
import type { ShopFilters } from '@/lib/shop-filters'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { loadCatalog } from '@/lib/catalog'
import { buildCustomRequestMessage } from '@/lib/whatsapp'
import { useReveal } from '@/hooks/use-reveal'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Sheet } from '@/components/generals/sheet'
import { ProductCard } from '@/components/shop/product-card'
import { WhatsAppLink } from '@/components/shop/whatsapp-link'
import { PRICE_RANGES, PRODUCT_LABELS, SORT_OPTIONS } from '@/constants/site'

import {
    ChevronDown,
    Search,
    SlidersHorizontal,
    X
} from 'lucide-react'

import {
    DEFAULT_FILTERS,
    applyFilters,
    filtersToQuery,
    parseFilters
} from '@/lib/shop-filters'

interface ShopProps {
    products: Array<Product>
    categories: Array<Category>
    settings: StoreSettings
    initialFilters: ShopFilters
}

interface FilterPanelProps {
    filters: ShopFilters
    categories: Array<Category>
    onChange: (patch: Partial<ShopFilters>) => void
}

const optionClass = (active: boolean) => cn(
    'flex cursor-pointer items-center justify-between rounded-2xl px-3 py-2.5 text-sm font-semibold transition',
    active ? 'bg-white text-cocoa-900 shadow-sm' : 'text-cocoa-700 hover:bg-white/60'
)

const FilterPanel: FC<FilterPanelProps> = ({ filters, categories, onChange }): ReactNode => {
    return (
        <div className='space-y-9'>
            <fieldset>
                <legend className='text-xs font-bold uppercase tracking-[0.22em] text-cocoa-500'>
                    Kategori
                </legend>
                <div className='mt-4 space-y-1'>
                    <label className={optionClass(!filters.category)}>
                        <span className='flex items-center gap-3'>
                            <input
                                type='radio'
                                name='category'
                                className='size-4 accent-[#4e3628]'
                                checked={!filters.category}
                                onChange={() => onChange({ category: null })}
                            />
                            Semua kategori
                        </span>
                    </label>
                    {categories.map(category => (
                        <label key={category.id} className={optionClass(filters.category === category.slug)}>
                            <span className='flex items-center gap-3'>
                                <input
                                    type='radio'
                                    name='category'
                                    className='size-4 accent-[#4e3628]'
                                    checked={filters.category === category.slug}
                                    onChange={() => onChange({ category: category.slug })}
                                />
                                <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                    {category.name}
                                </span>
                            </span>
                            <span className='text-xs text-cocoa-500'>
                                {category.productCount}
                            </span>
                        </label>
                    ))}
                </div>
            </fieldset>

            <fieldset>
                <legend className='text-xs font-bold uppercase tracking-[0.22em] text-cocoa-500'>
                    Koleksi
                </legend>
                <div className='mt-4 space-y-1'>
                    <label className={optionClass(!filters.label)}>
                        <span className='flex items-center gap-3'>
                            <input
                                type='radio'
                                name='label'
                                className='size-4 accent-[#4e3628]'
                                checked={!filters.label}
                                onChange={() => onChange({ label: null })}
                            />
                            Semua koleksi
                        </span>
                    </label>
                    {PRODUCT_LABELS.map(entry => (
                        <label key={entry.id} className={optionClass(filters.label === entry.id)}>
                            <span className='flex items-center gap-3'>
                                <input
                                    type='radio'
                                    name='label'
                                    className='size-4 accent-[#4e3628]'
                                    checked={filters.label === entry.id}
                                    onChange={() => onChange({ label: entry.id })}
                                />
                                {entry.label}
                            </span>
                        </label>
                    ))}
                </div>
            </fieldset>

            <fieldset>
                <legend className='text-xs font-bold uppercase tracking-[0.22em] text-cocoa-500'>
                    Harga
                </legend>
                <div className='mt-4 space-y-1'>
                    {[{ id: null, label: 'Semua harga' }, ...PRICE_RANGES].map(range => (
                        <label key={range.id ?? 'any'} className={cn(optionClass(filters.price === range.id), 'justify-start gap-3')}>
                            <input
                                type='radio'
                                name='price'
                                className='size-4 accent-[#4e3628]'
                                checked={filters.price === range.id}
                                onChange={() => onChange({ price: range.id })}
                            />
                            {range.label}
                        </label>
                    ))}
                </div>
            </fieldset>

            <fieldset>
                <legend className='text-xs font-bold uppercase tracking-[0.22em] text-cocoa-500'>
                    Ketersediaan
                </legend>
                <label className='mt-4 flex cursor-pointer items-start gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold text-cocoa-700 transition hover:bg-white/60'>
                    <input
                        type='checkbox'
                        className='mt-0.5 size-4 accent-[#4e3628]'
                        checked={filters.ready}
                        onChange={event => onChange({ ready: event.target.checked })}
                    />
                    <span>
                        Tersedia, siap kirim
                        <span className='mt-0.5 block text-xs font-normal text-muted-foreground'>
                            Sembunyikan produk pesanan khusus dan yang stoknya habis
                        </span>
                    </span>
                </label>
            </fieldset>
        </div>
    )
}

const Shop: FC<ShopProps> = ({ products, categories, initialFilters }): ReactNode => {
    const router = useRouter()
    const [filters, setFilters] = useState<ShopFilters>(initialFilters)
    const [searchText, setSearchText] = useState(initialFilters.q)
    const [panelOpen, setPanelOpen] = useState(false)
    const gridRef = useRef<HTMLDivElement>(null)
    const committedQuery = useRef(initialFilters.q)

    // Follow the address bar (header links, back and forward) without clobbering live typing.
    useEffect(() => {
        if (!router.isReady) return

        const next = parseFilters(router.query)

        setFilters(next)

        if (next.q !== committedQuery.current) {
            committedQuery.current = next.q
            setSearchText(next.q)
        }
    }, [router.isReady, router.query])

    const commit = (next: ShopFilters) => {
        committedQuery.current = next.q
        setFilters(next)
        router.replace({ pathname: '/shop', query: filtersToQuery(next) }, undefined, { shallow: true, scroll: false })
    }

    const update = (patch: Partial<ShopFilters>) => commit({ ...filters, ...patch })

    // Debounce typing into the search box before it touches the address bar.
    useEffect(() => {
        if (searchText === filters.q) return

        const timer = window.setTimeout(() => commit({ ...filters, q: searchText }), 280)

        return () => window.clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText])

    const results = useMemo(() => applyFilters(products, filters), [products, filters])
    const activeCategory = categories.find(category => category.slug === filters.category) ?? null
    const activeLabel = PRODUCT_LABELS.find(entry => entry.id === filters.label) ?? null
    const activePrice = PRICE_RANGES.find(range => range.id === filters.price) ?? null
    const hasFilters = Boolean(filters.q || filters.category || filters.label || filters.price || filters.ready)

    useReveal(gridRef, `${results.length}-${filters.category}-${filters.label}-${filters.price}-${filters.sort}-${filters.q}-${filters.ready}`)

    const title = activeLabel ? activeLabel.label : 'Semua Produk'
    const clearAll = () => {
        setSearchText('')
        commit({ ...DEFAULT_FILTERS, sort: filters.sort })
    }

    return (
        <Layout categories={categories}>
            <Seo
                title={activeCategory ? activeCategory.name : title}
                description={activeCategory?.description ?? 'Lihat semua boneka rajut handmade EMA CHARMS: boneka hewan, beruang, kelinci, karakter, gantungan kunci rajut, boneka custom, dan edisi terbatas. Harga dalam Rupiah, pesan lewat WhatsApp.'}
            />

            <section className='relative overflow-hidden bg-[radial-gradient(120%_120%_at_10%_0%,#fdf1f2_0%,#fffaf3_50%,#fbf1e8_100%)]'>
                <div aria-hidden='true' className='pointer-events-none absolute -right-24 top-10 size-72 rounded-full border-2 border-dashed border-blush-200' />
                <div className='relative mx-auto max-w-7xl px-5 pb-10 pt-14 sm:px-8 sm:pt-20'>
                    <nav aria-label='Breadcrumb' className='text-sm text-muted-foreground'>
                        <ol className='flex items-center gap-2'>
                            <li>
                                <Link href='/' className='hover:text-cocoa-900'>
                                    Beranda
                                </Link>
                            </li>
                            <li aria-hidden='true'>/</li>
                            <li className={cn(!activeCategory && 'font-semibold text-cocoa-800')}>
                                {activeCategory
                                    ? (
                                        <Link href='/shop' className='hover:text-cocoa-900'>
                                            Semua Produk
                                        </Link>
                                    )
                                    : 'Semua Produk'}
                            </li>
                            {activeCategory && (
                                <>
                                    <li aria-hidden='true'>/</li>
                                    <li className='font-semibold text-cocoa-800'>
                                        <span data-mdlfy-cms={`categories:${activeCategory.id}:name`}>
                                            {activeCategory.name}
                                        </span>
                                    </li>
                                </>
                            )}
                        </ol>
                    </nav>

                    <div className='mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                        <div className='max-w-2xl'>
                            <p className='text-xs font-bold uppercase tracking-[0.26em] text-blush-600'>
                                Handmade Crochet
                            </p>
                            <h1 className='mt-3 font-heading text-5xl leading-[1.02] tracking-[-0.02em] text-cocoa-900 font-soft sm:text-6xl'>
                                {activeCategory
                                    ? <span data-mdlfy-cms={`categories:${activeCategory.id}:name`}>{activeCategory.name}</span>
                                    : title}
                            </h1>
                            <p className='mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg'>
                                {activeCategory?.description
                                    ? <span data-mdlfy-cms={`categories:${activeCategory.id}:description`}>{activeCategory.description}</span>
                                    : 'Setiap boneka di halaman ini dirajut dengan tangan. Pilih favoritmu, tentukan warnanya, lalu pesan dalam beberapa ketukan lewat WhatsApp.'}
                            </p>
                        </div>

                        <form
                            role='search'
                            onSubmit={event => {
                                event.preventDefault()
                                commit({ ...filters, q: searchText })
                            }}
                            className='relative w-full lg:max-w-sm'
                        >
                            <label htmlFor='shop-search' className='sr-only'>
                                Cari Produk
                            </label>
                            <Search className='pointer-events-none absolute left-5 top-1/2 size-5 -translate-y-1/2 text-cocoa-500' />
                            <input
                                id='shop-search'
                                type='search'
                                value={searchText}
                                onChange={event => setSearchText(event.target.value)}
                                placeholder='Cari Produk: nama, warna, hewan...'
                                className='h-14 w-full rounded-full border border-input bg-white pl-14 pr-5 text-base shadow-sm outline-none transition focus:border-caramel-400 focus:ring-4 focus:ring-caramel-300/30'
                            />
                        </form>
                    </div>

                    <div className='no-scrollbar -mx-5 mt-10 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:px-0'>
                        <button
                            type='button'
                            onClick={() => update({ category: null })}
                            aria-pressed={!filters.category}
                            className={cn(
                                'shrink-0 rounded-full border px-4 py-2.5 text-sm font-semibold transition',
                                !filters.category ? 'border-cocoa-800 bg-cocoa-800 text-cream-50' : 'border-border bg-white text-cocoa-700 hover:border-cocoa-700/30'
                            )}
                        >
                            Semua Produk
                        </button>
                        {categories.map(category => (
                            <button
                                key={category.id}
                                type='button'
                                onClick={() => update({ category: category.slug })}
                                aria-pressed={filters.category === category.slug}
                                className={cn(
                                    'shrink-0 rounded-full border px-4 py-2.5 text-sm font-semibold transition',
                                    filters.category === category.slug ? 'border-cocoa-800 bg-cocoa-800 text-cream-50' : 'border-border bg-white text-cocoa-700 hover:border-cocoa-700/30'
                                )}
                            >
                                <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                    {category.name}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section aria-label='Daftar produk' className='mx-auto max-w-7xl px-5 pb-24 pt-10 sm:px-8'>
                <div className='grid gap-10 lg:grid-cols-[250px_1fr]'>
                    <aside aria-label='Filter' className='hidden lg:block'>
                        <div className='sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto rounded-[28px] bg-cream-100/70 p-5'>
                            <FilterPanel filters={filters} categories={categories} onChange={update} />
                            {hasFilters && (
                                <button
                                    type='button'
                                    onClick={clearAll}
                                    className='mt-8 w-full rounded-full border border-cocoa-700/20 py-2.5 text-sm font-semibold text-cocoa-700 transition hover:bg-white'
                                >
                                    Hapus semua filter
                                </button>
                            )}
                        </div>
                    </aside>

                    <div>
                        <div className='flex flex-wrap items-center justify-between gap-3'>
                            <p className='text-sm text-muted-foreground' aria-live='polite'>
                                <span className='font-bold text-cocoa-800'>{results.length}</span>
                                <span> produk</span>
                            </p>
                            <div className='flex items-center gap-2'>
                                <button
                                    type='button'
                                    onClick={() => setPanelOpen(true)}
                                    className='inline-flex h-11 items-center gap-2 rounded-full border border-border bg-white px-4 text-sm font-semibold text-cocoa-800 lg:hidden'
                                >
                                    <SlidersHorizontal className='size-4' />
                                    Filter
                                </button>
                                <label className='relative'>
                                    <span className='sr-only'>Urutkan</span>
                                    <select
                                        value={filters.sort}
                                        onChange={event => update({ sort: event.target.value })}
                                        className='h-11 appearance-none rounded-full border border-border bg-white pl-4 pr-10 text-sm font-semibold text-cocoa-800 outline-none focus:ring-2 focus:ring-caramel-300/50'
                                    >
                                        {SORT_OPTIONS.map(option => (
                                            <option key={option.id} value={option.id}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-cocoa-500' />
                                </label>
                            </div>
                        </div>

                        {hasFilters && (
                            <div className='mt-4 flex flex-wrap gap-2'>
                                {filters.q && (
                                    <button type='button' onClick={() => { setSearchText(''); update({ q: '' }) }} className='inline-flex items-center gap-1.5 rounded-full bg-blush-100 px-3 py-1.5 text-xs font-semibold text-cocoa-800'>
                                        Pencarian: {filters.q}
                                        <X className='size-3.5' />
                                    </button>
                                )}
                                {activeCategory && (
                                    <button type='button' onClick={() => update({ category: null })} className='inline-flex items-center gap-1.5 rounded-full bg-blush-100 px-3 py-1.5 text-xs font-semibold text-cocoa-800'>
                                        <span data-mdlfy-cms={`categories:${activeCategory.id}:name`}>{activeCategory.name}</span>
                                        <X className='size-3.5' />
                                    </button>
                                )}
                                {activeLabel && (
                                    <button type='button' onClick={() => update({ label: null })} className='inline-flex items-center gap-1.5 rounded-full bg-blush-100 px-3 py-1.5 text-xs font-semibold text-cocoa-800'>
                                        {activeLabel.label}
                                        <X className='size-3.5' />
                                    </button>
                                )}
                                {activePrice && (
                                    <button type='button' onClick={() => update({ price: null })} className='inline-flex items-center gap-1.5 rounded-full bg-blush-100 px-3 py-1.5 text-xs font-semibold text-cocoa-800'>
                                        {activePrice.label}
                                        <X className='size-3.5' />
                                    </button>
                                )}
                                {filters.ready && (
                                    <button type='button' onClick={() => update({ ready: false })} className='inline-flex items-center gap-1.5 rounded-full bg-blush-100 px-3 py-1.5 text-xs font-semibold text-cocoa-800'>
                                        Siap kirim
                                        <X className='size-3.5' />
                                    </button>
                                )}
                            </div>
                        )}

                        {results.length > 0
                            ? (
                                <div ref={gridRef} className='mt-8 grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 xl:grid-cols-3'>
                                    {results.map((product, index) => (
                                        <div key={product.id} data-reveal>
                                            <ProductCard product={product} priority={index < 3} />
                                        </div>
                                    ))}
                                </div>
                            )
                            : (
                                <div className='mt-10 flex flex-col items-center rounded-[32px] border border-dashed border-blush-300 bg-blush-50/60 px-6 py-16 text-center'>
                                    <p className='font-heading text-3xl text-cocoa-900'>
                                        {products.length ? 'Belum ada produk yang cocok' : 'Produk segera hadir'}
                                    </p>
                                    <p className='mt-3 max-w-md text-sm leading-relaxed text-muted-foreground'>
                                        {products.length
                                            ? 'Coba kata kunci atau rentang harga lain. Kalau kamu punya ide khusus, kami bisa merajutkannya untukmu.'
                                            : 'Kami sedang menyiapkan koleksi baru. Sementara itu, kamu bisa memesan boneka custom lewat WhatsApp.'}
                                    </p>
                                    <div className='mt-7 flex flex-wrap justify-center gap-3'>
                                        {products.length > 0 && (
                                            <button
                                                type='button'
                                                onClick={() => {
                                                    setSearchText('')
                                                    commit({ ...DEFAULT_FILTERS })
                                                }}
                                                className='inline-flex h-12 items-center rounded-full bg-cocoa-800 px-6 text-sm font-semibold text-cream-50 transition hover:bg-cocoa-700'
                                            >
                                                Tampilkan semua produk
                                            </button>
                                        )}
                                        <WhatsAppLink message={buildCustomRequestMessage} variant='outline'>
                                            Pesan boneka custom
                                        </WhatsAppLink>
                                    </div>
                                </div>
                            )}
                    </div>
                </div>
            </section>

            <Sheet open={panelOpen} onClose={() => setPanelOpen(false)} labelledBy='filters-title'>
                <div className='flex items-center justify-between border-b border-border px-6 py-5'>
                    <h2 id='filters-title' className='font-heading text-2xl text-cocoa-900'>
                        Filter
                    </h2>
                    <button
                        type='button'
                        onClick={() => setPanelOpen(false)}
                        aria-label='Tutup filter'
                        className='grid size-10 place-items-center rounded-full text-cocoa-800 hover:bg-muted'
                    >
                        <X className='size-5' />
                    </button>
                </div>
                <div className='flex-1 overflow-y-auto px-4 py-6'>
                    <FilterPanel filters={filters} categories={categories} onChange={update} />
                </div>
                <div className='flex gap-3 border-t border-border p-5'>
                    <button
                        type='button'
                        onClick={clearAll}
                        className='h-12 flex-1 rounded-full border border-cocoa-700/20 text-sm font-semibold text-cocoa-700'
                    >
                        Hapus
                    </button>
                    <button
                        type='button'
                        onClick={() => setPanelOpen(false)}
                        className='h-12 flex-[2] rounded-full bg-cocoa-800 text-sm font-semibold text-cream-50'
                    >
                        Tampilkan <span>{results.length}</span> produk
                    </button>
                </div>
            </Sheet>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<ShopProps> = async ({ query, res }) => {
    const { products, categories, settings } = await loadCatalog()

    res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=60')

    return {
        props: {
            products,
            categories,
            settings,
            initialFilters: parseFilters(query)
        }
    }
}

export default Shop
